import { logger } from '@ecosystem/newron-client-lib';
import { ContextSelectorMenu, ContextSettingSelectorProps, useEditContextSettings } from '@ecosystem/newron-core';
import { Entity, Periodicity, MarketPeriod as MarketPeriod2 } from '@ecosystem/newron-core/types';
import { actions, feedback, feedbackV2, inputs } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';

import { SortIcon } from '../assets/SortIcon';

import './markets.css';
import { Market, MarketPeriod } from './types';
import { useMobile } from '../providers/mobile/mobile.provider';
import classNames from 'classnames';
import { hasBetaFeatureFlag } from '../pages/utils';

const { Checkbox, CheckboxState, TextInput } = inputs;
const { Button } = actions;
const { DotsLoader, Tooltip } = feedback;
const { Alert } = feedbackV2;

const SEARCH_BAR_COUNT = 10;
const LOADING_OPACITY = 50;
const LOADED_OPACITY = 1;
const TOOLTIP_COUNT = 10;

type Header = {
  label: string;
  sortBy: SortBy;
};

enum SortBy {
  productGroup,
  country,
  lastMonth,
  lastWeek,
}

enum SortDirection {
  ascending = 'asc',
  descending = 'desc',
}

export function MarketSelector({ onClose, onChange }: ContextSettingSelectorProps) {
  const [isLoading, setLoading] = useState(true);

  const { isMobile } = useMobile();
  const isBeta = hasBetaFeatureFlag();
  // State from Context Settings
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<Market[]>([]);

  const { getContextSettings } = useEditContextSettings();

  useEffect(() => {
    const fetchSettingsAndUpdateState = async () => {
      try {
        setLoading(true);
        const settings = await getContextSettings(['openMarkets', 'cell']);
        const markets = extractMarkets(settings['openMarkets']);
        const cells = settings['cell'].values;

        setMarkets(markets);
        setSelectedMarkets(getSelectedMarkets(markets, cells));
      } catch (error) {
        logger.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettingsAndUpdateState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractMarkets = (openMarketsSetting: any): Market[] => {
    return openMarketsSetting.definition.selection.options.filter(opt => typeof opt !== 'string') as Market[];
  };

  const getSelectedMarkets = (markets: Market[], cells: { value: string }[]): Market[] => {
    return markets.filter(market => cells.some(cell => cell.value === market.value));
  };

  const onMarketCheckboxChange = (isSelected: boolean, market: Market) => {
    if (isSelected) {
      setSelectedMarkets(prev => [...prev, market]);
    } else {
      setSelectedMarkets(prev => prev.filter(p => p.value !== market.value));
    }
  };

  const AllMarketCheckboxChange = (state: inputs.CheckboxState, markets: Market[]) => {
    if (state === CheckboxState.UNCHECKED) {
      setSelectedMarkets([]);
    } else {
      setSelectedMarkets(markets);
    }
  };

  const handleView = () => {
    let cells: Entity[] = [];

    selectedMarkets.forEach(sm => {
      cells.push({ value: `${sm.countryCode}-${sm.productCode}`, label: `${sm.productName} in ${sm.countryName}` });
    });

    onChange({
      cell: cells,
    });

    onClose();
  };

  return (
    <ContextSelectorMenu>
      <div
        role="menu"
        style={{ maxHeight: !isMobile ? '80vh' : 'none' }}
        className={classNames(
          'relative flex-column flex w-192',
          `opacity-${isLoading ? LOADING_OPACITY : LOADED_OPACITY}`,
          { 'markets-table': isMobile, 'markets-table-beta': isBeta }
        )}
      >
        <>
          <MarketList
            isLoading={isLoading}
            markets={markets}
            selectedMarkets={selectedMarkets}
            onChange={onMarketCheckboxChange}
            onAllChange={AllMarketCheckboxChange}
          />
          <Footer handleView={handleView} handleCancel={onClose} selectedMarkets={selectedMarkets} />
        </>
      </div>
    </ContextSelectorMenu>
  );
}

type MarketListProps = {
  isLoading: boolean;
  markets: Market[];
  selectedMarkets: Market[];
  onChange: (state: boolean, market: Market) => void;
  onAllChange: (state: inputs.CheckboxState, markets: Market[]) => void;
};
function MarketList({ isLoading, markets, selectedMarkets, onChange, onAllChange }: MarketListProps) {
  const [search, setSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.productGroup);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);

  const { isMobile } = useMobile();

  const searchFilter = (market: Market) => {
    const countryName = market.countryName.toLowerCase();
    const productName = market.productName.toLowerCase();

    return countryName.includes(search.toLowerCase()) || productName.includes(search.toLowerCase());
  };

  const onSort = (header: Header) => {
    setSortDirection(prev => (prev === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending));
    setSortBy(header.sortBy);
  };

  const sortByColumn = (a: Market, b: Market, sortDirection: SortDirection) => {
    const getSequence = (market: Market, periodicity: Periodicity) => {
      return market.periods.find(p => p.periodicity === periodicity)?.sequence;
    };

    const comparePeriods = (per1: number, per2: number) => {
      if (per1 === undefined && per2 === undefined) return 0;
      if (per1 === undefined) return 1;
      if (per2 === undefined) return -1;

      return sortDirection === SortDirection.ascending ? per1 - per2 : per2 - per1;
    };

    const compareStrings = (str1: string, str2: string) => {
      return sortDirection === SortDirection.ascending ? str1.localeCompare(str2) : str2.localeCompare(str1);
    };

    switch (sortBy) {
      case SortBy.productGroup:
        return compareStrings(a.productName, b.productName) || compareStrings(a.countryName, b.countryName);
      case SortBy.country:
        return compareStrings(a.countryName, b.countryName) || compareStrings(a.productName, b.productName);
      case SortBy.lastMonth:
        const sequenceMonthlyA = getSequence(a, Periodicity.Monthly);
        const sequenceMonthlyB = getSequence(b, Periodicity.Monthly);
        return comparePeriods(sequenceMonthlyA, sequenceMonthlyB);
      case SortBy.lastWeek:
        const sequenceWeeklyA = getSequence(a, Periodicity.Weekly);
        const sequenceWeeklyB = getSequence(b, Periodicity.Weekly);
        return comparePeriods(sequenceWeeklyA, sequenceWeeklyB);
    }
  };
  const headers: Header[] = [
    {
      label: 'Product group',
      sortBy: SortBy.productGroup,
    },
    {
      label: 'Country',
      sortBy: SortBy.country,
    },
    {
      label: 'Last month',
      sortBy: SortBy.lastMonth,
    },
    {
      label: 'Last week',
      sortBy: SortBy.lastWeek,
    },
  ];

  const marketRows = markets.filter(searchFilter).sort((a, b) => sortByColumn(a, b, sortDirection));

  return (
    <>
      <div className={classNames('my-2', { 'mx-4': !isMobile, 'mx-2': isMobile })}>
        {markets.length >= SEARCH_BAR_COUNT && <TextInput placeholder="Search" onChange={setSearch} autoFocus />}
      </div>
      <div className="flex-grow overflow-y-auto pb-6 overscroll-contain cursor-none">
        <table className="w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {isMobile && <col className="w-6" />}
            <col className="w-28" />
            <col className="w-18" />
            <col className="w-19" />
            <col className="w-19" />
          </colgroup>
          <thead className="sticky top-0 text-left bg-white z-10">
            <tr>
              {isMobile && (
                <th className="sticky left-0 top-0 pb-1 px-3 w-6">
                  <Checkbox
                    state={selectedMarkets.length === 0 ? CheckboxState.UNCHECKED : CheckboxState.CHECKED}
                    onChange={state => onAllChange(state, marketRows)}
                  />
                </th>
              )}
              <th onClick={() => onSort(headers[0])} className="pb-1 px-3 w-60">
                <div className="flex cursor-pointer">
                  {!isMobile && (
                    <Checkbox
                      state={selectedMarkets.length === 0 ? CheckboxState.UNCHECKED : CheckboxState.CHECKED}
                      onChange={state => onAllChange(state, marketRows)}
                    />
                  )}
                  <div className="pl-3">{headers[0].label}</div>
                  <SortIcon sizePx={24} direction={sortBy === headers[0].sortBy && sortDirection} />
                </div>
              </th>
              {headers.slice(1).map(header => (
                <th key={header.label} onClick={() => onSort(header)} className="pb-2">
                  <div className={`flex cursor-pointer`}>
                    {header.label}
                    <SortIcon sizePx={24} direction={sortBy === header.sortBy && sortDirection} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {marketRows.map(market => {
              const periodLabel = (period: MarketPeriod | MarketPeriod2) => (
                <div
                  className={classNames('text-lighter', { 'overflow-ellipses': isMobile })}
                  style={{
                    fontStyle: period?.sequence ? 'normal' : 'italic',
                  }}
                >
                  {(isMobile ? period?.name.replace(/\b(\d{4})\b/g, match => match.slice(-2)) : period?.name) ||
                    'not subscribed'}
                </div>
              );

              const tableData = (children: string | React.JSX.Element) => (
                <td
                  className={classNames('py-1', {
                    'text-2lines': isMobile && typeof children === 'string',
                  })}
                  style={{ verticalAlign: 'text-top' }}
                >
                  {children}
                </td>
              );

              const isSelected = selectedMarkets.some(
                sm => sm.countryCode == market.countryCode && sm.productCode === market.productCode
              );

              return (
                <tr
                  key={market.value}
                  onClick={() => onChange(!isSelected, market)}
                  className={`${isSelected && 'bg-brand-light'} hover:bg-brand-light cursor-pointer`}
                >
                  {isMobile && (
                    <td className="sticky left-0 px-3 py-1">
                      <Checkbox
                        onChange={state => onChange(state === CheckboxState.CHECKED, market)}
                        state={isSelected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                      />
                    </td>
                  )}
                  <td className="px-3 py-1">
                    <div className="flex">
                      {!isMobile && (
                        <Checkbox
                          onChange={state => onChange(state === CheckboxState.CHECKED, market)}
                          state={isSelected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                        />
                      )}
                      <span className={classNames('pl-3', { 'text-2lines': isMobile })}>{market.productName}</span>
                    </div>
                  </td>
                  {tableData(market.countryName)}
                  {tableData(periodLabel(market.periods.find(p => p.periodicity === Periodicity.Monthly)))}
                  {tableData(periodLabel(market.periods.find(p => p.periodicity === Periodicity.Weekly)))}
                </tr>
              );
            })}
            {/* Empty and Loading states */}
            {marketRows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5}>
                  <div className="mx-2 mt-4">
                    <Alert title="No markets found" type="info" />
                  </div>
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={4}>
                  <DotsLoader />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

type FooterProps = {
  handleView: () => void;
  handleCancel: () => void;
  selectedMarkets: Market[];
};
function Footer({ handleView, handleCancel, selectedMarkets }: FooterProps) {
  const { isMobile } = useMobile();

  const MarketsSelected = (
    <Tooltip placement="bottom">
      <span className="underline">{selectedMarkets.length} markets</span> selected
      <Tooltip.Content>
        {selectedMarkets
          .map(sm => sm.label)
          .slice(0, TOOLTIP_COUNT)
          .join(', ')}
        {selectedMarkets.length > TOOLTIP_COUNT && '...'}
      </Tooltip.Content>
    </Tooltip>
  );

  return (
    <div
      className={classNames(
        'flex h-18 items-center justify-between border-t border-light px-4 py-4 flex items-end gap-3 bg-white',
        { 'sticky bottom-0 bg-white': isMobile }
      )}
    >
      <Button mode="secondary" onClick={handleCancel}>
        Cancel
      </Button>
      <div className={classNames({ 'flex items-center': isMobile })}>
        <span className="text-lighter text-base mr-2">
          {selectedMarkets.length !== 0 ? MarketsSelected : 'Select any number of product groups'}
        </span>
        {selectedMarkets.length !== 0 && <Button onClick={handleView}>View selected</Button>}
      </div>
    </div>
  );
}
